import {Box} from "@mui/material";
import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {toast} from "react-toastify";
import {get, post} from "../../common/network";
import FallBackFormMessage from "../../components/common/fallback-form-message/FallbackFormMessage";
import FormSearchFilter from "../../components/common/form-search-filter/FormSearchFilter";
import ModalPreview from "../../components/common/modal-preview/ModalPreview";
import MuiTableWrapper from "../../components/common/mui-table-wrapper/MuiTableWrapper";
import SidebarTagsDrawer from "../../components/common/sidebar-tags-wrapper/SidebarTagsDrawer";
import SidebarTagsWrapper from "../../components/common/sidebar-tags-wrapper/SidebarTagsWrapper";
import StarCheckbox from "../../components/common/star-checkbox/StarCheckbox";
import AssignAttorney from "../../components/common/modal-preview/AssignAttorney";
import MultiFilter from "../../components/multi-filter/Multifilter";
import { getNestedValue, normalizeDate } from "../../utils/CommonMethods";
import { FormTypeOptions } from "../../constants";

const FormsListing = () => {
  //#region for states
  const [selectedRows, setSelectedRows] = useState([]); // Array of the selected rows
  const [favFormList, setFavFormsList] = useState([]); // Array of favourite form
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [combinedData, setCombinedData] = useState({});
  const [data, setData] = useState([]);
  const [isFetching, setIsFetching] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [formTypeValue, setFormTypeValue] = useState("");
  const [dateValue, setDateValue] = useState("");
  const [rowsSelection,setRowsSelection] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isAssignAttorneyModalOpen, setIsAssignAttorneyModalOpen] = useState(false);
  const [dynamicFilters, setDynamicFilters] = useState([
      { column: '', operator: '', value: [], from: null, to: null }
  ]);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [filteredData, setFilteredData] = useState([]);

  //#region for hooks
  const navigate = useNavigate();
  const location = useLocation();

  const multiFilterColumns = [
      {
          field: "form_name",
          headerName: "Form Name",
          type: "text",
      },
      {
          field: "form_code",
          headerName: "Form Code",
          type: "text",
      },
      {
          field: "form_type",
          headerName: "Type",
          type: "select",
          options: FormTypeOptions
      },
      {
          field: "country",
          headerName: "Country",
          type: "text",
      },
      {
          field: "categoryName",
          headerName: "Category",
          type: "select",
          options: categories
      },
  ];
  
  //getting selected rows from redirect
  let selectedRowsFromState = location.state?.selectedRows || [];
  const [selectedRowsUpper, setSelectedRowsUpper] = useState(selectedRowsFromState);

    useEffect(() => {
      const combinedDataFunction = selectedRowsUpper.flatMap((matter) =>
            selectedRows.map((form) => ({matter_id: matter.id, form_id: form.id}))
        );
        setCombinedData(combinedDataFunction);
    }, [selectedRowsUpper, selectedRows]);

  useEffect(() => {
    if (selectedRowsUpper == 0) {
      navigate(-1);
    }

    selectedRowsFromState = selectedRowsUpper;
  }, [selectedRowsUpper]);


  useEffect(() => {
      setFilteredData(data);
  }, [data]);

  const handlePreviewModalClose = () => {
    setIsPreviewModalOpen(false);
  };
  //#endregion

  const handleCloseAssignAttorneyModal = () => {
        setIsAssignAttorneyModalOpen(false);
  };

    
  const applyMultiFilters = (rows, filters) => {
      if (!filters || !filters.length) return rows;

      return rows.filter(row =>
          filters.every(filter => {
              const { column, operator, value, from, to } = filter;
              // const rowValue = row[column];
              const rowValue = getNestedValue(row, column);

              // EMPTY CHECKS
              if (operator === 'isEmpty') {
                  return rowValue === null || rowValue === undefined || rowValue === '';
              }

              if (operator === 'isNotEmpty') {
                  return rowValue !== null && rowValue !== undefined && rowValue !== '';
              }

              // DATE RANGE
              if ((operator === 'equals' || operator === 'notEquals') && from) {
                  if (!rowValue || !from) return false;

                  const rowDate = new Date(rowValue).toISOString().split('T')[0];
                  const filterDate = new Date(from).toISOString().split('T')[0];

                  return operator === 'equals'
                      ? rowDate === filterDate
                      : rowDate !== filterDate;
              }

              if (operator === 'between') {
                  if (!rowValue || !from || !to) return false;

                  const rowDate = new Date(rowValue);

                  const startDate = new Date(from);
                  startDate.setHours(0, 0, 0, 0);

                  const endDate = new Date(to);
                  endDate.setHours(23, 59, 59, 999); 

                  return rowDate >= startDate && rowDate <= endDate;
              }

              // MULTI-SELECT DROPDOWN
              if (Array.isArray(value) && value.length > 0) {
                  if (Array.isArray(rowValue)) {
                      if (operator === 'doesNotContains' || operator === 'notEquals') {
                      return !rowValue.some(v => value.includes(v));
                      }
                      return rowValue.some(v => value.includes(v));
                  }

                  if (operator === 'doesNotContains' || operator === 'notEquals') {
                      return !value.includes(rowValue);
                  }

                  return value.includes(rowValue);
              }
              
              // If value array is empty, show all rows
              if (Array.isArray(value) && value.length === 0) {
                  return true;
              }

              // TEXT COMPARISON
              const cell = String(rowValue ?? '').toLowerCase();
              const search = String(value ?? '').toLowerCase();

              switch (operator) {
                  case 'contains':
                      return cell.includes(search);
                  case 'doesNotContains':
                      return !cell.includes(search);
                  case 'equals':
                      return cell === search;
                  case 'notEquals':
                      return cell !== search;
                  case 'startsWith':
                      return cell.startsWith(search);
                  case 'endsWith':
                      return cell.endsWith(search);
                  default:
                      return true;
              }
          })
      );
  };

  const handleApplyFilters = (filters) => {
      // Clear single search state
      setSearchValue("");
      setFilterText("");
      setFormTypeValue("");
      setDateValue("");
      setSearchResetKey(prev => prev+1);
      // Store active multi-filters
      setDynamicFilters(filters);

      const normalizedFilters = filters.map(f => ({
          ...f,
          from: normalizeDate(f.from),
          to: normalizeDate(f.to)
      }));
      const result = applyMultiFilters(data, normalizedFilters);
      setFilteredData(result);
  };

  const fetchCategories = async () => {
      try {
          const response = await get(`docgen/categories/`, {}, false);
          if (response?.status === 200) {
              const result = (response?.data || []).map(item => ({
                  value: item.id,
                  label: item.name
              }));
              setCategories(result);
          } 
      }
      catch (err) {
      }
  }

  const columns = [
    {
      field: "form_name",
      header: "Form Name",
      render: (value, row) => {
        return (
          <div 
          style={{textDecoration:"underline", cursor:"pointer",paddingTop:"0.5%", paddingBottom:"0.5%"}}
            onClick={() => {
                window.open(`/docgen/form/details?id=${row.id}`);
              }}>
             {value}   
          </div>
        );
      },
    },
    {
      field: "form_code",
      header: "Form Code",
    },
    {
      field: "form_type",
      header: "Type",
    },
    {
      field: "country",
      header: "Country",
    },
    {
      field: "favourite",
      header: "",
      filter: false,
      sort: false,
      render: (value, row) => {
        return (
          <StarCheckbox
            isSelected={
              row.favorite_form_details[0]?.form_id === row.id ? true : favFormList.includes(row.id)
            }
            changeEvent={() => {
              if (favFormList.includes(row.id) || row.favorite_form_details[0]?.form_id) {
                handleUnFavorite(row.id);
              } else {
                handleFavorite(row.id);
              }
            }}
          />
        );
      },
    },
  ];

  const handleFavorite = async (id) => {
      try {
        const response = await post(`docgen/forms/favorite`, {
          formIds: [{ formId: id, status: true }],
        });
        if (response?.success === true) {
          toast.success(response.message);
          setFavFormsList((prevList) => [...prevList, id]);
        }
      } catch (error) {
      }
  };

  const handleUnFavorite = async (id) => {
      try {
        const response = await post(`docgen/forms/favorite`, {
          formIds: [{ formId: id, status: false }],
        });
        if (response?.success === true) {
          setFavFormsList((prevList) => prevList.filter((form) => form !== id));
          toast.success(response.message);
        }
      } catch (error) {
      }
  };

  const handleSearch = (value) => {
    setDynamicFilters([{ column: '', operator: '', value: [], from: null, to: null }]);
    const columnName = value?.value || ""; // Name of the column
    const searchValue = value?.searchedText || ""; // Value entered in the input field
    const dropdownValue = value?.dropdownValue || ""; // value selected in the dropdown field
    setSearchValue(searchValue);
    setFilterText(columnName);
    setFormTypeValue(dropdownValue);

    if (columnName === "updated_at") {
      setDateValue(value);
    }
    setFilteredData(data);
  };

  const fetchData = async () => {
    setIsFetching(true);
    try {
      const response = await get(
        `docgen/forms`                
      );
      setData(response.data);
    } catch (error) {
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, []);

  //#endregion

  return (
    <SidebarTagsWrapper
      active={true} // Default open
      component={
        <SidebarTagsDrawer
          upperSectionProps={{
            title: "Docket Selected",
            setSelectedRows: setSelectedRowsUpper,
            selectedRows: selectedRowsUpper,
            tagName: "name",
          }}
          upperSubSectionProps={{
              title: "Assign Attorney",
              setIsAssignAttorneyModalOpen: setIsAssignAttorneyModalOpen
          }}
          lowerSectionProps={{
            title: "Forms Selected",
            setSelectedRows: setSelectedRows,
            selectedRows: selectedRows,
            tagName: "code",
          }}
          buttonProps={{
            title: "Generate Preview",
            disabled: selectedRows.length === 0 ? true : false,
            onClick: () => {
              navigate("/docgen/matters/preview", {
                state: {selectedRows, selectedRowsFromState: selectedRowsUpper, combinedData}
              });
            },
          }}
        />
      }
    >
      <div className="page-content-wrapper">
        <Box className="w-100">
          <header className="section-heading">
            <div className="left-area">
              <FormSearchFilter
                options={[
                  { value: "", label: "All" },
                  { value: "form_name", label: "Form Name" },
                  { value: "form_code", label: "Form Code" },
                  { value: "form_type", label: "Type" },
                  { value: "country", label: "Country" },
                  {value: "categoryName", label: "Category"}
                ]}
                defaultOption = { {value: "", label: "Select an option"} }
                dateFiltersOptionsValueArray={[]}
                dropdownArray={["form_type", "categoryName"]}
                dropdownOptions={{
                  form_type: [
                    {
                      value: "standard",
                      label: "Standard",
                    },
                    {
                      value: "custom",
                      label: "Custom",
                    },
                  ],
                  categoryName: categories
                }}
                onSearch={(values) => handleSearch(values)}
                searchResetKey={searchResetKey}
              />
            </div>
            <div className="right-area">
              <section className="top-actions-section">
                  <div className="buttons-group">
                    <MultiFilter 
                        filters={dynamicFilters}
                        setFilters={setDynamicFilters}
                        columns={multiFilterColumns}
                        onAddFilter={handleApplyFilters} 
                    />
                  </div>
              </section>
            </div>
          </header>
          {/* <div style={{ height: '90vh', overflow: 'auto' }}> */}
            <MuiTableWrapper
              columns={columns}
              fetchData={fetchData}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              colgroup={[
                  {width: "50%"},
                  {width: "20%"},
                  {width: "10%"},
                  {width: "10%"}
              ]}
              emptyBody={
                <FallBackFormMessage searchedKeyword={searchValue}/>
              }
              searchValue={searchValue}
              filterColumn={filterText}
              formTypeValue={formTypeValue}
              data={filteredData}
              isFetching={isFetching}
              dateValue={dateValue}
              rowsSelection={rowsSelection}
              setRowsSelection={setRowsSelection}
            />
          {/* </div> */}
        </Box>
        {
          // PREVIEW BUTTON MODAL POPUP
          <ModalPreview
            isModalOpen={isPreviewModalOpen}
            handleModalClose={handlePreviewModalClose}
          />
        }
      </div>
      <AssignAttorney
          openModal={isAssignAttorneyModalOpen}
          closeModal={handleCloseAssignAttorneyModal}
          selectedDockets={selectedRowsUpper}
          setSelectedDockets={setSelectedRowsUpper}
      />
    </SidebarTagsWrapper>
  );
};

export default FormsListing;